import React, { useEffect, useState } from 'react';
import { Skeleton } from '@mui/material';

import type { ScaleTime } from 'd3-scale';
import type { ConditionRating } from 'types/conditionRating';
import type { LiveWindPoint } from 'types/liveWind';
import type { WindTimestep } from 'types/wind';

import LineWindGraphLegend from './LiveWindGraphLegend';
import useSetupGraphCanvas from './useSetupGraphCanvas';
import styles from './LiveWindLineGraph.module.scss';

interface Props {
  liveWind: Array<LiveWindPoint>;
  forecastedWind: Array<WindTimestep>;
  ratings: Array<ConditionRating>;
  xScale: ScaleTime<number, number>;
  canvasRef: React.MutableRefObject<HTMLDivElement>;
  currentMarkerXPercentage: number;
}

const LiveWindLineGraph: React.FC<Props> = ({
  liveWind,
  ratings,
  forecastedWind,
  xScale,
  canvasRef,
  currentMarkerXPercentage,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    // This graph uses some slow logic to draw the dotted lines for points that do not have
    // `sensorWind: true`. As part of that logic we have to do a binary search on all the points and
    // then calculate the length of the line, and some other logic. This logic is synchronous and
    // will block the main thread. In order to avoid blocking the main thread during rendering of
    // items around the graph we 1use this `isMounted` state to avoid rendering the graph until after
    // a timeout (which allows tangential rendering to happen). This is a hacky solution, but it
    // prevents things like the switch not flipping.
    const timeout = setTimeout(() => setIsMounted(true), 200);

    return () => clearTimeout(timeout);
  }, []);

  const { setupGraph } = useSetupGraphCanvas({
    liveWind,
    forecastedWind,
    ratings,
    xScale,
    canvasRef,
    currentMarkerXPercentage,
  });

  useEffect(() => {
    if (isMounted) {
      setupGraph();
    }
  }, [setupGraph, isMounted]);

  return (
    <>
      {isMounted ? (
        <div className={styles.graph} data-testid="live-wind-line-graph-container" />
      ) : (
        <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
      )}
      <LineWindGraphLegend />
    </>
  );
};

export default LiveWindLineGraph;
